/**
 * IraReasonCodesPage — Reference Data · IRA Reason Codes.
 *
 * The catalog of reasons an IRA distribution can be processed for. The
 * Distribution wizard reads this list, and each row maps to an IRS Form
 * 1099-R box 7 distribution code that lands on the customer's year-end
 * statement — so the box-7 mapping is IRS-reporting-sensitive and must be
 * confirmed against the latest IRS Pub 590-B before edits.
 *
 * Contract (verified against the backend 2026-06-16 — shared-types model,
 * config read route, upsert DTO, and service all share identical camelCase
 * field names; NO drift found):
 *   - Read:   GET /v3/config/ira-reason-codes
 *             → V3ConfigFunction.IraReasonCodes (ApiResult<IraReasonCode[]>).
 *             Returns ACTIVE rows only (.Where(r => r.IsActive)), ordered by
 *             SortOrder, Code. Inactive rows are not surfaced.
 *   - Write:  POST /v3/admin/ira-reason-codes
 *             → AdminSystemService.UpsertIraReasonCodeAsync (EF createOrUpdate,
 *             natural key Code). Service persists EVERY field on both the
 *             create and update branches (no silent drop).
 *   - Soft-delete: re-upsert isActive=false. A hard DELETE route exists
 *             (admin.iraReasonCodes.remove) but it EF-Removes the row, which
 *             would orphan historical IraDistribution / 1099-R rebuild rows
 *             that reference Code — so the UI deactivates instead of deleting.
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/iraReasonCodesEditor.tsx — the
 * operational app writes no config). Field names + write/read routes are the
 * verbatim verified wire contract.
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type IraReasonCodeUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface ReasonRow {
  code: string;
  displayName: string;
  form1099Box7Code: string;
  form5498Box: string | null;
  minAge: number | null;
  maxAge: number | null;
  requiresExceptionDocumentation: boolean;
  withholdingDefaultFedPct: number | null;
  withholdingDefaultStatePct: number | null;
  isActive: boolean;
  sortOrder: number;
  notes: string | null;
}

interface EditDraft extends ReasonRow {
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return {
    code: '',
    displayName: '',
    form1099Box7Code: '',
    form5498Box: null,
    minAge: null,
    maxAge: null,
    requiresExceptionDocumentation: false,
    withholdingDefaultFedPct: 10,
    withholdingDefaultStatePct: 0,
    isActive: true,
    sortOrder: 100,
    notes: '',
    isNew: true,
  };
}

function rowToDraft(r: ReasonRow): EditDraft {
  return { ...r, isNew: false };
}

function draftToUpsertBody(d: EditDraft): IraReasonCodeUpsertBody {
  return {
    code: d.code.trim(),
    displayName: d.displayName.trim(),
    form1099Box7Code: d.form1099Box7Code.trim(),
    form5498Box: d.form5498Box,
    minAge: d.minAge,
    maxAge: d.maxAge,
    requiresExceptionDocumentation: d.requiresExceptionDocumentation,
    withholdingDefaultFedPct: d.withholdingDefaultFedPct,
    withholdingDefaultStatePct: d.withholdingDefaultStatePct,
    isActive: d.isActive,
    sortOrder: d.sortOrder,
    notes: d.notes,
  };
}

function fmtAgeRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return 'Any age';
  if (min != null && max == null) return `${min}+`;
  if (min == null && max != null) return `≤ ${max}`;
  return `${min}–${max}`;
}

function fmtPct(n: number | null): string {
  if (n == null) return '—';
  return `${n.toFixed(2)}%`;
}

const CODE_PATTERN = /^[A-Z0-9_]{1,32}$/;

export function IraReasonCodesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<ReasonRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReasonRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.iraReasonCodes();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: ReasonRow[] = resp.data.map((r) => ({
            code: r.code,
            displayName: r.displayName,
            form1099Box7Code: r.form1099Box7Code ?? '',
            form5498Box: r.form5498Box ?? null,
            minAge: r.minAge ?? null,
            maxAge: r.maxAge ?? null,
            requiresExceptionDocumentation: r.requiresExceptionDocumentation,
            withholdingDefaultFedPct: r.withholdingDefaultFedPct ?? null,
            withholdingDefaultStatePct: r.withholdingDefaultStatePct ?? null,
            isActive: r.isActive,
            sortOrder: r.sortOrder,
            notes: r.notes ?? null,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load IRA reason codes.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const sorted = (rows ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (r) =>
        r.code.toLowerCase().includes(needle) ||
        r.displayName.toLowerCase().includes(needle) ||
        r.form1099Box7Code.toLowerCase().includes(needle),
    );
  }, [rows, filter]);

  const upsert = useCallback(
    async (d: EditDraft): Promise<boolean> => {
      const resp = await client.retail.admin.iraReasonCodes.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(d),
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const code = draft.code.trim();
    if (!code || !CODE_PATTERN.test(code)) {
      setSaveError('Code must be 1–32 chars (uppercase letters, digits, underscore).');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Plain-language name is required.');
      return;
    }
    if (draft.minAge != null && draft.maxAge != null && draft.maxAge < draft.minAge) {
      setSaveError('Max age must be ≥ min age.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created IRA reason code "${code}".`
          : `Updated IRA reason code "${code}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-disable via re-upsert: historical IraDistribution / 1099-R rows
      // reference Code, so a hard delete would orphan year-end rebuilds.
      await upsert({ ...deleteTarget, isActive: false, isNew: false });
      setToast({
        tone: 'warn',
        text: `${deleteTarget.code} (${deleteTarget.displayName}) is now inactive. The Distribution wizard won't offer it.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<ReasonRow>> = useMemo(
    () => [
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.code}</code> },
      { header: 'Plain-language name', cell: (r) => <span className="cell-strong">{r.displayName}</span> },
      {
        header: '1099-R box 7',
        cell: (r) => <code className="cell-mono">{r.form1099Box7Code || '—'}</code>,
      },
      { header: 'Age range', cell: (r) => <span className="num">{fmtAgeRange(r.minAge, r.maxAge)}</span> },
      {
        header: 'Fed w/h default',
        cell: (r) => <span className="num">{fmtPct(r.withholdingDefaultFedPct)}</span>,
      },
      {
        header: "Doc req'd?",
        cell: (r) =>
          r.requiresExceptionDocumentation ? <Pill tone="warn">yes</Pill> : <Pill tone="muted">no</Pill>,
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load IRA reason codes: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading IRA reason codes…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        These reason codes drive IRA distribution flows and 1099-R reporting. Each row maps a
        plain-language reason to an IRS Form 1099-R box 7 code. Update only after confirming against
        the latest IRS Publication 590-B distribution-code guidance &mdash; a wrong box-7 mapping
        flows straight to the customer&apos;s tax statement.
      </Banner>

      <Card
        title={`IRA Reason Codes — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="code, name, or box-7 code…"
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Reason
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          The Distribution wizard reads from this list (active rows only). Lower sort order renders
          first in the wizard radio list.
        </p>
        <DataTable<ReasonRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.code}
          emptyState={<p className="empty">No reason codes match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add an IRA reason code' : `Edit ${draft?.code ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Code"
                value={draft.code}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({
                    ...draft,
                    code: v.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 32),
                  })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. NORMAL, EARLY_NOEXC"
                hint={draft.isNew ? 'Up to 32 chars (uppercase, digits, underscore). Permanent.' : 'Permanent.'}
              />
              <TextField
                label="1099-R box 7 code"
                value={draft.form1099Box7Code}
                onChange={(v) => setDraft({ ...draft, form1099Box7Code: v.toUpperCase().slice(0, 4) })}
                monospace
                placeholder="e.g. 7, 1, 2, 4, G, T"
                hint="IRS Form 1099-R box 7 distribution code."
              />
            </div>

            <TextField
              label="Plain-language name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v.slice(0, 128) })}
              placeholder="e.g. Normal distribution (age 59½+)"
              hint="Shown to the officer in the Distribution wizard radio cards."
            />

            <div className="form-row">
              <NumberField
                label="Min age"
                value={draft.minAge}
                onChange={(v) => setDraft({ ...draft, minAge: v == null ? null : Math.max(0, Math.min(120, v)) })}
                min={0}
                max={120}
                placeholder="—"
                hint="Inclusive minimum owner age this reason permits."
              />
              <NumberField
                label="Max age"
                value={draft.maxAge}
                onChange={(v) => setDraft({ ...draft, maxAge: v == null ? null : Math.max(0, Math.min(120, v)) })}
                min={0}
                max={120}
                placeholder="—"
                hint="Inclusive maximum owner age this reason permits."
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Fed withholding default %"
                value={draft.withholdingDefaultFedPct}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    withholdingDefaultFedPct: v == null ? null : Math.max(0, Math.min(100, v)),
                  })
                }
                min={0}
                max={100}
                step={0.5}
                placeholder="10"
                hint="Default federal withholding percentage."
              />
              <NumberField
                label="State withholding default %"
                value={draft.withholdingDefaultStatePct}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    withholdingDefaultStatePct: v == null ? null : Math.max(0, Math.min(100, v)),
                  })
                }
                min={0}
                max={100}
                step={0.5}
                placeholder="0"
                hint="Default state withholding (officer can override per owner request)."
              />
            </div>

            <div className="form-row">
              <TextField
                label="5498 box"
                value={draft.form5498Box ?? ''}
                onChange={(v) => setDraft({ ...draft, form5498Box: v.toUpperCase().slice(0, 8) || null })}
                monospace
                placeholder="—"
                hint="Form 5498 box code for rollover/contribution tracking. Optional."
              />
              <NumberField
                label="Sort order"
                value={draft.sortOrder}
                onChange={(v) => setDraft({ ...draft, sortOrder: v == null ? 0 : Math.max(0, Math.min(9999, v)) })}
                min={0}
                max={9999}
                hint="Lower numbers render first in the wizard radio list."
              />
            </div>

            <BoolField
              label="Documentation required"
              value={draft.requiresExceptionDocumentation}
              onChange={(v) => setDraft({ ...draft, requiresExceptionDocumentation: v })}
              hint="Officer must collect supporting paperwork (physician statement, court order, etc.) before the post."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive reasons hide from the Distribution wizard but stay for historical 1099-R rebuilds."
            />

            <TextArea
              label="Notes"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v || null })}
              rows={3}
              placeholder="e.g. Owner at or above the IRS RMD-eligible age. Standard taxable distribution."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this reason inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark <code className="cell-mono">{deleteTarget.code}</code> (
              {deleteTarget.displayName}) inactive. The Distribution wizard won&apos;t offer it as a
              choice anymore, but historical distribution rows that reference it stay intact. Flip
              it back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
